import { faHand } from '@fortawesome/free-regular-svg-icons';
import { faArrowDown, faArrowUp, faArrowUpShortWide, faBan, faCalendar, faCaretDown, faCaretUp, faClock, faDownload, faDownLong, faEye, faInfoCircle, faLayerGroup, faList, faLocationDot, faQuestionCircle, faX, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ActiveLayersOpacity, ActiveLayersReordering, OutletIProps } from '../../../../components/sitebase/Base.types';
import IS_EFAS from '../../../../components/sitebase/Environment';
import { MapActiveLayer, TimelineStateI, WMSLayer } from '../../Map.types';
import { hasDimension, keywordValue } from '../../maputils';
import { LayerToggle } from '../LayerToggle';
import DssCountryComponent from '../../../glofas/dss/DssCountryComponent';
import ForecastDate from './ForecastDate';
import { groupFullName } from './LayerSelector.utils';
import { LayerSelectorViews } from './LayerSelector.types';

import './LayerSelector.css';
import { useOutletContext } from 'react-router-dom';

const TimeView = memo(function (props: LayerSelectorProps & { groupStates: { [key: string]: { folded: boolean, toggled: boolean } }, setGroupStates: React.Dispatch<React.SetStateAction<{ [key: string]: { folded: boolean, toggled: boolean } }>> }) {
    const { setTimelineState } = props;
    const groupedByTimeStep = useMemo(() => {
        return props.activeLayers.reduce((acc, layer) => {
            if (layer.dimensions === undefined || !(layer.dimensions instanceof Array)) {
                if (!acc["None"]) acc["None"] = [];
                acc["None"].push(layer);
                return acc;
            }
            const reftimeDim = layer.dimensions.find(dim => dim['name'] === "reftime" || dim['name'] === "SEASONAL_TIME");
            if (!reftimeDim) {
                if (!acc["None"]) acc["None"] = [];
                acc["None"].push(layer);
                return acc;
            }
            // assuming the format is like "P1M", "P1W", "P1D", "PT1H"
            const timeStep = reftimeDim['values'].substring(reftimeDim['values'].length - 2);
            if (!acc[timeStep]) acc[timeStep] = [];
            acc[timeStep].push(layer);
            return acc;
        }, {} as { [key: string]: MapActiveLayer[] });
    }, [props.activeLayers]);

    // if groupedByTimeStep has only 1 group then it should be set as the timeline state
    useEffect(() => {
        const groups = Object.entries(groupedByTimeStep);
        if (groups.length > 0) {
            const resultGroups = groups.filter(([group, _]) => group !== "None");
            if (resultGroups && resultGroups.length === 1) {
                const group = resultGroups[0];
                if (setTimelineState) setTimelineState({
                    interval: group[0],
                    layers: group[1]
                });
            }
        }
    }, [groupedByTimeStep, setTimelineState]);

    const timeStepEntries = Object.entries(groupedByTimeStep);
    const hasAnimatedGroups = timeStepEntries.some(([group]) => group !== "None");

    return <>
        {timeStepEntries.map(([group, layers], i) => {
            const groupKey = `${group}_${i}`;
            const state = props.groupStates[groupKey] || { folded: group === "None", toggled: true };

            return <div key={groupKey}>
                <div>
                    <GroupViewItem
                        extraActionButton={group !== "None" && props.setTimelineState && <div className='btn btn-outline-dark fs-9 d-flex flex-row p-1'
                            onClick={() => {
                                if (group !== "None" && props.setTimelineState) props.setTimelineState({
                                    interval: group,
                                    layers: layers
                                })
                            }}
                        >Send to timeline <FontAwesomeIcon icon={faDownLong} /></div> || null}
                        folded={state.folded}
                        toggled={state.toggled}
                        onFoldedChange={(folded) => props.setGroupStates(prev => ({ ...prev, [groupKey]: { ...state, folded } }))}
                        onToggledChange={(toggled) => props.setGroupStates(prev => ({ ...prev, [groupKey]: { ...state, toggled } }))}
                        currentForecastDate={props.currentForecastDate}
                        index={i}
                        group={`${groupFullName(group)} animation`}
                        layers={layers}
                        activeLayers={layers}
                        onRemoveLayer={props.onRemoveLayer}
                        onToggleLayer={props.onToggleLayer}
                        onShowLegend={props.onShowLegend}
                        onUpdateDimension={props.onUpdateDimension}
                        onSendLayerToTop={props.onSendLayerToTop}
                        onChangeOpacity={props.onChangeOpacity}
                        opacityPresentation={props.opacityPresentation}
                        onClearAllFeatures={props.onClearAllFeatures}
                        onRemoveAllLayers={props.onRemoveAllLayers}
                        onToggleAllLayers={props.onToggleAllLayers}
                        view={props.view}
                        setView={props.setView} />
                </div>
            </div>;
        })}
        {!hasAnimatedGroups && <div className='border-top mt-2 px-3 py-3'>
            <p className='mb-0 text-muted' style={{ fontSize: '0.85em' }}>
                You have no animated layers on,{' '}
                <span
                    role='button'
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => props.setView(LayerSelectorViews.LIST)}
                >
                    click here
                </span>
                {' '}to go back to the List view.
            </p>
        </div>}
    </>;
});

const GroupViewItem = memo(function (props: {
    group: string,
    index: number,
    layers: MapActiveLayer[],
    extraActionButton?: React.JSX.Element | null,
    folded: boolean,
    toggled: boolean,
    onFoldedChange: (folded: boolean) => void,
    onToggledChange: (toggled: boolean) => void,
    currentForecastDate: string,
    onSetForecastDateToLayers?: (layers: MapActiveLayer[], date: string) => void,
}
    & LayerSelectorProps) {
    const folded = props.folded;
    const toggled = props.toggled;

    const [forecastDate, setForecastDate] = useState<string>(props.currentForecastDate);
    const [countriesWindowOpen, setCountriesWindowOpen] = useState<boolean>(false);

    useEffect(() => {
        setForecastDate(props.currentForecastDate);
    }, [props.currentForecastDate]);

    return <div key={props.group + props.index}>
        {props.layers.some(layer => layer.name.toLowerCase().includes("dss")) && (
            <DssCountryComponent
                forecastTime={forecastDate}
                visible={countriesWindowOpen}
                onClose={() => setCountriesWindowOpen(false)}
            />
        )}
        <div className='fst-italic text-center border-top pt-1 my-auto d-flex flex-row'>
            <span className='mb-0 ms-2'>
                <LayerToggle className='d-inline-block' id={props.group + '-toggle-all'} toggled={toggled}
                    onClick={() => {
                        props.layers.forEach((layer) => props.onToggleLayer(layer.id, !toggled));
                        props.onToggledChange(!toggled);
                    }}

                />
            </span>

            <div className="d-flex flex-row mx-auto">
                <span onClick={() => props.onFoldedChange(!folded)}>
                    <span>{props.group}</span>
                    <span><FontAwesomeIcon icon={!folded ? faCaretDown : faCaretUp} /></span>
                </span>
                {props.layers.some(layer => layer.name.toLowerCase().includes("dss")) && (
                    <Tooltip title="View country data" placement="bottom" arrow disableInteractive>
                        <span
                            role="button"
                            className="btn btn-outline-dark fs-9 d-flex flex-row p-1 ms-1"
                            onClick={() => setCountriesWindowOpen(true)}
                        >
                            Countries
                        </span>
                    </Tooltip>
                )}
                {props.extraActionButton && <span>{props.extraActionButton}</span>}
            </div>
            {props.onSetForecastDateToLayers && (props.group === "GFM" || props.group === "Monitoring" || props.group === "DSS") &&
                < ForecastDate
                    currentForecastDate={forecastDate}
                    onSetForecastDate={(date: string) => {
                        if (props.onSetForecastDateToLayers) props.onSetForecastDateToLayers(props.layers, date);
                        setForecastDate(date);
                    }}
                    className='d-flex'
                    hidePreviousAndNextLast={true}
                    showTime={false}
                />}
        </div>
        {!folded && <div>
            <ListView
                currentForecastDate={forecastDate}
                activeLayers={props.layers}
                onRemoveLayer={props.onRemoveLayer}
                onToggleLayer={props.onToggleLayer}
                onSendLayerToTop={props.onSendLayerToTop}
                onShowLegend={props.onShowLegend}
                onUpdateDimension={props.onUpdateDimension}
                onChangeOpacity={props.onChangeOpacity}
                opacityPresentation={props.opacityPresentation}
                view={props.view}
                setView={props.setView}
            />
        </div>}
    </div>;
});


const GroupView = memo(function (props: LayerSelectorProps & { groupStates: { [key: string]: { folded: boolean, toggled: boolean } }, setGroupStates: React.Dispatch<React.SetStateAction<{ [key: string]: { folded: boolean, toggled: boolean } }>> }) {
    // Group layers by group and sort by thename of the group
    const groupedLayers = props.activeLayers.reduce((acc, layer) => {
        if (!acc[layer.group]) acc[layer.group] = [];
        acc[layer.group].push(layer);
        return acc;
    }, {} as { [key: string]: MapActiveLayer[] });

    const grouped = Object.keys(groupedLayers).sort().reduce((acc, key) => {
        acc[key] = groupedLayers[key];
        return acc;
    }, {} as { [key: string]: MapActiveLayer[] });

    return Object.entries(grouped).map(([group, layers], i) => {
        const state = props.groupStates[group] || { folded: false, toggled: true };
        return <GroupViewItem
            key={group}
            group={group}
            layers={layers}
            index={i}
            folded={state.folded}
            toggled={state.toggled}
            onFoldedChange={(folded) => props.setGroupStates(prev => ({ ...prev, [group]: { ...state, folded } }))}
            onToggledChange={(toggled) => props.setGroupStates(prev => ({ ...prev, [group]: { ...state, toggled } }))}
            {...props}
        />;
    });
});

function ListViewItem(props: {
    border: boolean,
    layer: MapActiveLayer,
} & LayerSelectorProps) {
    const context = useOutletContext<OutletIProps>();
    const dragReorderLayer = props.onDragReorderLayer;
    const sendToTop = props.onSendLayerToTop;
    const [opacity, setOpacity] = useState<number>(props.layer.layer.getOpacity());
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [label, setLabel] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const testSafeLayerName = props.layer.name.replace(/[^a-zA-Z0-9_-]/g, '-');

    const layer_allowed = !props.forecastEmbargoActive || ((IS_EFAS && props.layer.layerInfo.Keywords && props.layer.layerInfo.Keywords["public"] === "true" ? true : false));

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('layerId', props.layer.id);
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (e.preventDefault) {
            e.preventDefault();
        }
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
        return false;
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
        setIsDragOver(false);

        const draggedLayerId = e.dataTransfer.getData('layerId');
        const targetId = props.layer.id;
        if (props.onDragReorderLayer && draggedLayerId !== targetId) {
            const targetIndex = props.activeLayers.findIndex(l => l.id === targetId);
            const draggedIndex = props.activeLayers.findIndex(l => l.id === draggedLayerId);
            const draggedLayer = props.activeLayers[draggedIndex];

            if (props.onDragReorderLayer) props.onDragReorderLayer(draggedLayer, targetIndex);
        }

        return true;
    };

    const processLabel = useCallback((layerInfo: WMSLayer) => {
        if (layerInfo.Keywords && "label" in layerInfo.Keywords) {
            // label is in format <action>.<values...>
            const rawLabel = layerInfo.Keywords['label'] || "";
            const parts = rawLabel.split('.');
            let result = "";
            for (let i = 0; i < parts.length; i += 2) {
                const action = parts[i];
                const value = parts[i + 1];

                switch (action) {
                    case "fetch":
                        return (fetch(`${value}${props.currentForecastDate}`).then(res => res.json()).then(data => {
                            if (Array.isArray(data) && data.length > 0) {
                                setLabel(`[${moment.utc(data[0]['date'], "YYYYMMDDHH").format("YYYY-MM-DD (06 UTC)")}]`);
                            }
                        }));
                    case "addDays": {
                        const daysToAdd = parseInt(value);
                        result += moment.utc(props.currentForecastDate).add(daysToAdd, 'days').format("YYYY-MM-DD");
                        break;
                    }
                    case "append":
                        result += " " + value;
                        break;
                }
            }
            setLabel(`[${result}]`);
        }
        return null;
    }, [props.currentForecastDate]);

    useEffect(() => {
        processLabel(props.layer.layerInfo);
    }, [props.layer, props.currentForecastDate, processLabel]);

    return <div
        key={props.layer.id}
        className={`d-flex flex-column ps-2 pt-2 ${props.border ? 'border-bottom' : ''}`}
        data-testid={`layer-item-${testSafeLayerName}`}
        data-layer-name={props.layer.name}
        style={{
            opacity: !layer_allowed ? 0.5 : isDragging ? 0.4 : 1,
            backgroundColor: isDragOver ? '#faa73e33' : 'transparent',
            transition: 'opacity 0.2s, background-color 0.2s',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
    >
        <div className='lh-1 mt-1 d-flex flex-row'>
            <div className='fw-bold fs-8 w-75'>
                {keywordValue(props.layer.layerInfo.Keywords, 'download') === "true" && <Tooltip title="Downloadable layer" placement="bottom" arrow disableInteractive><span className='pe-1'><FontAwesomeIcon icon={faDownload} onClick={() => {
                    const appName = IS_EFAS ? 'efas' : 'glofas';
                    const tokenQuery = IS_EFAS ? `?token=${context.user.token}` : '';
                    window.open(`https://european-flood.emergency.copernicus.eu/api/fms/download/${appName}/${props.layer.name}/${props.currentForecastDate}/${tokenQuery}`, '_blank');
                }} /></span></Tooltip>}
                {props.layer.displayName}
                {!layer_allowed && <Tooltip title="Disabled due to 30-day forecast embargo" placement="bottom" arrow disableInteractive><span className='ps-1' style={{ fontSize: '0.75em' }}><FontAwesomeIcon icon={faBan} /></span></Tooltip>}
            </div>
            <div className='ms-auto d-flex flex-row'>
                {keywordValue(props.layer.layerInfo.Keywords, 'queryable') === "true" && <Tooltip title="Queryable layer" placement="bottom" arrow disableInteractive><span className='pe-1'><FontAwesomeIcon icon={faQuestionCircle} /></span></Tooltip>}
                {hasDimension(props.layer.dimensions, 'time') && <Tooltip title="Forecast layer - changes with date" placement="bottom" arrow disableInteractive><span className='pe-1'><FontAwesomeIcon icon={faCalendar} /></span></Tooltip>}
                {(hasDimension(props.layer.dimensions, 'reftime') || hasDimension(props.layer.dimensions, 'SEASONAL_TIME')) && <Tooltip title="Navigate in timeline" placement="bottom" arrow disableInteractive>
                    <div className='pe-1' onClick={() => props.setView(LayerSelectorViews.TIME)}><FontAwesomeIcon icon={faClock} /> </div>
                </Tooltip>}
            </div>
        </div>
        {label && <div className='fw-bold fs-9' style={{ color: "grey" }}>{label}</div>}
        <div className='fs-8 fw-lighter fst-italic'>{props.layer.source.includes(window.location.hostname) ? "" : props.layer.source}</div>

        <div className='d-flex flex-column fs-8'>
            <div className="d-flex flex-row fs-8">
                <Tooltip title="Show/hide layer" placement="bottom" arrow disableInteractive>
                    <div
                        className=''
                        key={props.layer.id + "-toggle-" + props.layer.layer.getVisible()}
                        data-testid={`layer-toggle-${testSafeLayerName}`}
                    >
                        <LayerToggle className='py-1 ps-1' id={props.layer.id} toggled={props.layer.layer.getVisible()} onClick={() => props.onToggleLayer(props.layer.id)} />
                    </div>
                </Tooltip>
                <Tooltip title="Remove layer" placement="bottom" arrow disableInteractive>
                    <div
                        role='button'
                        className='px-2 my-auto py-1 efas-layer-selector-button'
                        style={{ color: '#ff0000a1' }}
                        data-testid={`layer-remove-${testSafeLayerName}`}
                        onClick={() => { props.onRemoveLayer(props.layer.id) }}
                    ><FontAwesomeIcon icon={faX} /></div>
                </Tooltip>

                {sendToTop && <Tooltip title="Send layer to top" placement="bottom" arrow disableInteractive>
                    <div role='button' className='px-2 my-auto py-1 efas-layer-selector-button' style={{ color: 'green' }} onClick={() => { sendToTop(props.layer) }}><FontAwesomeIcon icon={faArrowUpShortWide} /></div>
                </Tooltip>}

                {props.reorderingPresentation === ActiveLayersReordering.ARROWS && props.onReorderLayer &&
                    <>
                        <Tooltip title="Move layer up" placement="bottom" arrow disableInteractive>
                            <div role='button' className='px-2 my-auto py-1 efas-layer-selector-button' style={{ color: 'green' }} onClick={() => { if (props.onReorderLayer) props.onReorderLayer(props.layer, 1) }}><FontAwesomeIcon icon={faArrowUp} /></div>
                        </Tooltip>
                        <Tooltip title="Move layer down" placement="bottom" arrow disableInteractive>
                            <div role='button' className='px-2 my-auto py-1 efas-layer-selector-button' style={{ color: 'green' }} onClick={() => { if (props.onReorderLayer) props.onReorderLayer(props.layer, -1) }}><FontAwesomeIcon icon={faArrowDown} /></div>
                        </Tooltip>
                    </>}

                <Tooltip title="Show legend" placement="bottom" arrow disableInteractive>
                    <div
                        role='button'
                        className='px-2 my-auto py-1 efas-layer-selector-button'
                        style={{ color: 'cornflowerblue' }}
                        data-testid={`layer-legend-${testSafeLayerName}`}
                        data-layer-name={props.layer.name}
                        onClick={() => { props.onShowLegend(props.layer) }}
                    ><FontAwesomeIcon icon={faInfoCircle} /></div>
                </Tooltip>
                {props.opacityPresentation === ActiveLayersOpacity.DROPDOWN && <Tooltip title="Adjust opacity" placement="top" arrow disableInteractive>
                    <div role='button' className='my-auto'>
                        <div
                            className='px-2 py-1 efas-layer-selector-button'
                            style={{ color: 'darkslategray' }}
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </div>
                        <Popover
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <div style={{ padding: '12px' }}>
                                <div className='d-flex flex-row'>
                                    <label className='my-auto' htmlFor={props.layer.id}>{opacity}</label>
                                    <input
                                        id={props.layer.id}
                                        type='range'
                                        className='efas-opacity-slider mx-2'
                                        style={{ width: "10rem" }}
                                        min={0.0}
                                        max={1.0}
                                        step={0.1}
                                        onChange={(ev) => {
                                            const newOpacity = parseFloat(ev.target.value);
                                            setOpacity(newOpacity);
                                            props.onChangeOpacity(props.layer, newOpacity);
                                        }}
                                        defaultValue={opacity} />
                                </div>
                            </div>
                        </Popover>
                    </div>
                </Tooltip>}
                {props.reorderingPresentation === ActiveLayersReordering.DRAGGING && dragReorderLayer && <Tooltip title="Drag to reorder" placement="bottom" arrow disableInteractive open={isDragging ? false : undefined}>
                    <div
                        role='button'
                        className='ms-auto px-2 my-auto py-1 fs-6'
                        style={{ color: 'orange', cursor: isDragging ? 'grabbing' : 'grab', marginRight: "2.5rem" }}
                        draggable={true}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <FontAwesomeIcon icon={faHand} />
                    </div>
                </Tooltip>}
            </div>
            {props.opacityPresentation === ActiveLayersOpacity.ALWAYS_VISIBLE && <div className='d-flex flex-row'>
                <Tooltip title="Set layer opacity" disableInteractive><input
                    id={props.layer.id}
                    type='range'
                    className='efas-opacity-slider m-2 mb-3'
                    min={0.0}
                    max={1.0}
                    step={0.1}
                    onChange={(ev) => {
                        const newOpacity = parseFloat(ev.target.value);
                        setOpacity(newOpacity);
                        props.onChangeOpacity(props.layer, newOpacity);
                    }}
                    defaultValue={opacity} />
                </Tooltip>
            </div>}
        </div>
    </div>;
};

const ListView = memo(function ListView(props: LayerSelectorProps) {
    return props.activeLayers.map((layerInfo, index) => {
        return <ListViewItem key={layerInfo.id} layer={layerInfo} border={index !== props.activeLayers.length - 1} {...props} />;
    })
});

interface LayerSelectorProps {
    currentForecastDate: string,
    activeLayers: MapActiveLayer[],
    onRemoveLayer: (id: string) => void,
    view: LayerSelectorViews,
    setView: (view: LayerSelectorViews) => void,
    onReorderLayer?: (layer: MapActiveLayer, direction: number) => void,
    onDragReorderLayer?: (layer: MapActiveLayer, newOrder: number) => void,
    onSendLayerToTop?: (layer: MapActiveLayer) => void,
    onToggleLayer: (id: string, visible?: boolean) => void,
    onToggleAllLayers?: (visible: boolean) => void,
    onRemoveAllLayers?: () => void,
    onShowLegend: (layer: MapActiveLayer) => void,
    onChangeOpacity: (layer: MapActiveLayer, opacity: number) => void,
    onUpdateDimension: (layer: MapActiveLayer, dimension: string, value: string) => void,
    onClearAllFeatures?: () => void,
    onSetForecastDateToLayers?: (layers: MapActiveLayer[], date: string) => void,
    versionMessage?: string,
    timelineState?: TimelineStateI,
    setTimelineState?: (state: TimelineStateI) => void,
    forecastEmbargoActive?: boolean,
    opacityPresentation?: ActiveLayersOpacity,
    reorderingPresentation?: ActiveLayersReordering
}
export default function LayerSelector(props: LayerSelectorProps) {
    const [allLayersToggled, setAllLayersToggled] = useState<boolean>(true);
    const [groupStates, setGroupStates] = useState<{ [key: string]: { folded: boolean, toggled: boolean } }>({});
    // const SMALL_DISPLAY = useSmallDisplay();

    if (!props.currentForecastDate) { return null; }

    return (<div className='d-flex flex-column p-1'
        style={{
            maxHeight: "calc(100vh - 10rem)",
            backgroundColor: '#f4f4f4d2'
        }}>
        <div className='d-flex flex-row'>
            <div className='d-flex flex-column ps-2'>
                <div className="d-flex flex-row">
                    <span className='text-uppercase fw-bold fs-6 me-1'>Layers</span>
                    <Tooltip title="Clear open points and markers" placement="bottom" arrow disableInteractive>
                        <div role='button' id='remove-all-map-markers' onClick={() => props.onClearAllFeatures && props.onClearAllFeatures()}><FontAwesomeIcon className='text-danger' icon={faLocationDot} /></div>
                    </Tooltip>
                </div>
                {IS_EFAS && <span style={{ fontSize: "10px" }} className='text-body-tertiary'>[EFAS forecasts from 2015-09-16 (00 UTC)] - [{props.versionMessage}]</span>}
            </div>
            <div className='d-flex flex-column ms-auto ps-4'>
                <div className='d-flex flex-row ms-auto my-auto ps-4 pe-3'>
                    <Tooltip title="Toggle the status of all layers" placement="bottom" arrow disableInteractive>
                        <div role='button'
                            id='toggle-all-layers'
                            className='my-auto'>
                            <LayerToggle id='toggle-all-layers' toggled={allLayersToggled}
                                onClick={() => {
                                    if (props.onToggleAllLayers) props.onToggleAllLayers(!allLayersToggled);
                                    setAllLayersToggled(!allLayersToggled);
                                }}
                            />
                        </div>
                    </Tooltip>
                    <Tooltip title="Remove all layers" placement="bottom" arrow disableInteractive>
                        <div
                            id='remove-all-layers'
                            role='button'
                            className='btn btn-outline-dark fs-8 p-1 mx-auto my-auto me-2'
                            style={{ backgroundColor: "white", color: "#a90329" }}
                            onClick={() => {
                                if (props.onRemoveAllLayers) props.onRemoveAllLayers();
                            }}>
                            <FontAwesomeIcon icon={faXmark} />
                        </div>
                    </Tooltip>
                </div>
                <div className='d-flex flex-row fs-8 mx-auto'>
                    <Tooltip title='List view — allows reordering' placement='bottom' arrow disableInteractive>
                        <div
                            role='button'
                            id='layer-selector-view-list'
                            className={`px-2 py-1 efas-layer-selector-button ${props.view === LayerSelectorViews.LIST ? 'active' : ''}`}
                            onClick={() => props.setView(LayerSelectorViews.LIST)}>
                            <FontAwesomeIcon icon={faList} />
                        </div>
                    </Tooltip>
                    <Tooltip title='Group view — grouped by layer name' placement='bottom' arrow disableInteractive>
                        <div
                            role='button'
                            id='layer-selector-view-group'
                            className={`px-2 py-1 efas-layer-selector-button ${props.view === LayerSelectorViews.GROUP ? 'active' : ''}`}
                            onClick={() => props.setView(LayerSelectorViews.GROUP)}>
                            <FontAwesomeIcon icon={faLayerGroup} />
                        </div>
                    </Tooltip>
                    <Tooltip title='Timeline view — grouped by time step' placement='bottom' arrow disableInteractive>
                        <div
                            role='button'
                            id='layer-selector-view-time'
                            className={`px-2 py-1 efas-layer-selector-button ${props.view === LayerSelectorViews.TIME ? 'active' : ''}`}
                            onClick={() => props.setView(LayerSelectorViews.TIME)}>
                            <FontAwesomeIcon icon={faClock} />
                        </div>
                    </Tooltip>
                </div>
            </div>
        </div>
        <div className='' style={{
            userSelect: 'none', overflowY: "auto", overflowX: "hidden",
            maxHeight: "calc(100vh - 25rem)"
        }}>
            {IS_EFAS ?
                <>
                    {props.view === LayerSelectorViews.TIME && <TimeView {...props} groupStates={groupStates} setGroupStates={setGroupStates} />}
                    {props.view === LayerSelectorViews.GROUP && <GroupView {...props} groupStates={groupStates} setGroupStates={setGroupStates} />}
                </> :
                <>
                    {props.view === LayerSelectorViews.GROUP && <GroupView {...props} groupStates={groupStates} setGroupStates={setGroupStates} />}
                    {props.view === LayerSelectorViews.TIME && <TimeView {...props} groupStates={groupStates} setGroupStates={setGroupStates} />}
                </>
            }
            {props.view === LayerSelectorViews.LIST && <ListView {...props} />}
        </div>
    </div >);
};